/**
 * useCancelEltJobExecution Hook
 *
 * Custom React Query mutation hook for cancelling job executions.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { jobExecutionService } from "../../../api/job-execution.service";
import { getErrorMessage } from "../../../lib/utils/errors";
import { JOB_EXECUTIONS_QUERY_KEY } from "./useEltJobExecutions";

interface UseCancelEltJobExecutionOptions {
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

/**
 * Hook for cancelling job execution
 * @param tenantId - Tenant ID
 * @param options - Success/error callbacks
 */
export const useCancelEltJobExecution = (tenantId: string, options?: UseCancelEltJobExecutionOptions) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (jobExecutionId: string) => jobExecutionService.cancelEltJobExecution(tenantId, jobExecutionId),
    onSuccess: () => {
      // Invalidate job executions list query
      queryClient.invalidateQueries({ queryKey: [JOB_EXECUTIONS_QUERY_KEY, tenantId] });
      options?.onSuccess?.();
    },
    onError: (error: unknown) => {
      const errorMessage = getErrorMessage(error);
      options?.onError?.(errorMessage);
    },
  });
};
